import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EEntityType, ERelationType, IFellowTravelerDto } from '@web/dtos';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import type { ICompanionDataForm } from '../../../pages/shop/direct-ticket-configuration-page/direct-ticket-owner-data-page/direct-ticket-owner-data-page.component';
import { TextInputComponent } from '../form-inputs/text-input/text-input.component';
import { DropDownComponent } from '../form-inputs/drop-down/drop-down.component';
import { FellowTravelerStore } from '../../../services/api/fellow-traveler/fellow-traveler-store.service';
import { Subscription } from 'rxjs';
import { AuthProvider } from '../../../services/auth/auth.provider';
import { AuthProviderFactory } from '../../../services/auth/auth-provider.factory';

@Component({
  selector: 'aw-direct-ticket-companion-data-form',
  imports: [
    ReactiveFormsModule,
    TextInputComponent,
    AppendTestIdPipe,
    FilterTextsPipe,
    LanguagePipe,
    DropDownComponent,
  ],
  templateUrl: './direct-ticket-companion-data-form.component.html',
})
export class DirectTicketCompanionDataFormComponent
  implements OnInit, OnDestroy
{
  public readonly companionForm: InputSignal<FormGroup<ICompanionDataForm>> =
    input.required();
  public readonly companionIndex: InputSignal<number> = input.required();
  public readonly id: InputSignal<string> = input.required();

  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');
  protected readonly fellowTravelerStore: FellowTravelerStore =
    inject(FellowTravelerStore);

  public readonly fellowTravelers: Signal<IFellowTravelerDto[]> =
    this.fellowTravelerStore.fellowTravelers;

  protected readonly fellowTravelerOptions: Signal<
    { label: string; value: string }[]
  > = computed(() =>
    this.fellowTravelers()
      .filter(
        (
          fellowTraveler: IFellowTravelerDto
        ): fellowTraveler is IFellowTravelerDto & { id: string } =>
          Boolean(fellowTraveler.id)
      )
      .map(
        (
          fellowTraveler: IFellowTravelerDto & { id: string }
        ): { label: string; value: string } => ({
          label: `${fellowTraveler.firstName} ${fellowTraveler.lastName}`,
          value: fellowTraveler.id,
        })
      )
  );

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  private readonly subscription: Subscription = new Subscription();

  ngOnInit(): void {
    this.subscription.add(
      this.companionForm().controls.fellowTravelerId.valueChanges.subscribe(
        (fellowTravelerId: string | undefined) => {
          const fellowTraveler = this.fellowTravelers().find(
            traveler => traveler.id === fellowTravelerId
          );

          const form = this.companionForm().controls;
          if (fellowTraveler) {
            form.firstName.setValue(fellowTraveler.firstName);
            form.lastName.setValue(fellowTraveler.lastName);
          } else {
            form.firstName.setValue('');
            form.lastName.setValue('');
            [form.firstName, form.lastName].forEach(control => {
              control.markAsPristine();
              control.markAsUntouched();
            });
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
